/**
 * المرحلة 1.4 — «زياراتي»: the patient's own visits, as their doctors
 * released them.
 *
 * What a visit shows is the institution's `patientVisitSummary` policy: once
 * the doctor releases it (or by itself when the visit closes), the sections
 * the policy names — the complaint, the examination, the diagnosis, the plan,
 * the readings. The papers issued to the patient (a certificate, a referral)
 * are always here to print; the full report downloads only where the policy
 * says `download`. Nothing here is the patient's to change.
 */

import { useState } from 'react';
import { Button, EmptyState, ErrorState, Icon, Spinner } from '../../components/common';
import { encounterApi } from '../../api/endpoints';
import { useAsync } from '../../hooks/useAsync';
import { useNotifications } from '../../context/NotificationContext';
import { diagnosisLabel } from '../../domain/encounter';
import { formatDate } from '../../domain/locale';
import { useI18n } from '../../i18n/I18nProvider';
import { printCertificate, printReferral, printVisitReport } from '../encounter/prints';

const SECTION_ORDER = ['subjective', 'objective', 'assessment', 'plan'];
const MODE_ICON = { in_person: 'user-round', video: 'video', voice: 'phone' };
const fmt = (n) => (Number.isInteger(Number(n)) ? String(Number(n)) : String(Math.round(Number(n) * 10) / 10));

export function VisitCard({ visit }) {
  const { t, lang } = useI18n();
  const { error: toastError } = useNotifications();
  const [busy, setBusy] = useState(null);
  const s = visit.summary;
  const doctor = visit.doctor || {};

  const run = async (key, work) => {
    setBusy(key);
    try {
      await work();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className="myv-card" data-testid="my-visit">
      <header className="myv-head">
        <Icon name={MODE_ICON[visit.mode] || 'user-round'} size={18} className="text-cyan-300" />
        <strong>{doctor.name || t('myVisits.doctor')}</strong>
        {doctor.title && <span>{doctor.title}</span>}
        <span>{formatDate(visit.date, { lang })}</span>
        {visit.clinicName && <span>{visit.clinicName}</span>}
      </header>

      {s ? (
        <>
          {SECTION_ORDER.filter((k) => s.sections && s.sections[k]).map((k) => (
            <div key={k} className="myv-sec">
              <h4>{t(`myVisits.sections.${k}`)}</h4>
              <p>{s.sections[k]}</p>
            </div>
          ))}
          {(s.diagnoses || []).length > 0 && (
            <div className="myv-sec">
              <h4>{t('visitPrint.diagnoses')}</h4>
              <p>{s.diagnoses.map((d) => diagnosisLabel(d, lang)).join('\n')}</p>
            </div>
          )}
          {(s.vitals || []).length > 0 && (
            <div className="myv-sec">
              <h4>{t('visitPrint.vitals')}</h4>
              <div className="myv-vitals">
                {s.vitals.map((v) => (
                  <span key={v.id || `${v.kind}-${v.at}`} className="enc-chip">
                    {t(`visitPrint.kinds.${v.kind}`)}: <bdi dir="ltr">{fmt(v.value)} {v.unit}</bdi>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-slate-400">{t('myVisits.notReleased')}</p>
      )}

      {((visit.certificates || []).length > 0 || (visit.referrals || []).length > 0 || visit.report) && (
        <div className="myv-papers">
          {(visit.certificates || []).map((c) => (
            <Button
              key={`c${c.id}`}
              variant="subtle"
              icon={busy === `c${c.id}` ? 'loader-2' : 'file-signature'}
              disabled={busy === `c${c.id}`}
              onClick={() => run(`c${c.id}`, () => printCertificate(c.id, { t, lang }))}
              data-testid="my-visit-certificate"
            >
              {t(`visitPrint.certificateKinds.${c.kind}`)}
              {c.status === 'revoked' ? ` (${t('myVisits.revoked')})` : ''}
            </Button>
          ))}
          {(visit.referrals || []).map((r) => (
            <Button
              key={`r${r.id}`}
              variant="subtle"
              icon={busy === `r${r.id}` ? 'loader-2' : 'send'}
              disabled={busy === `r${r.id}`}
              onClick={() => run(`r${r.id}`, () => printReferral(r.id, { t, lang }))}
              data-testid="my-visit-referral"
            >
              {t('myVisits.referralTo', { name: (lang === 'en' && r.partner && r.partner.nameEn) || (r.partner && r.partner.name) || '' })}
            </Button>
          ))}
          {visit.report && (
            <Button
              icon={busy === 'report' ? 'loader-2' : 'download'}
              disabled={busy === 'report'}
              onClick={() => run('report', () => printVisitReport(visit.id, { t, lang }))}
              data-testid="my-visit-report"
            >
              {t('myVisits.report')}
            </Button>
          )}
        </div>
      )}
    </article>
  );
}

/** Every released visit, or (with `bookingId`) the one of a booking. */
export function MyVisits({ bookingId = null }) {
  const { t } = useI18n();
  const state = useAsync(() => encounterApi.mine(), [], { initialData: null });
  if (state.loading && !state.data) return <Spinner label={t('myVisits.loading')} />;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;
  const all = (state.data && state.data.visits) || [];
  const visits = bookingId ? all.filter((v) => String(v.bookingId) === String(bookingId)) : all;
  if (!visits.length) {
    return <EmptyState icon="clipboard-check" title={bookingId ? t('myVisits.noneForBooking') : t('myVisits.emptyTitle')} message={t('myVisits.emptyText')} />;
  }
  return (
    <div className="myv-list" data-testid="my-visits">
      {visits.map((v) => (
        <VisitCard key={v.id} visit={v} />
      ))}
    </div>
  );
}

export default MyVisits;
